import React, { useMemo, useRef, useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ChevronLeft, ChevronRight } from "lucide-react";

import { ReactComponent as Line } from "../../assets/images/line.svg";
import { ReactComponent as Share2 } from "../../assets/images/Share2.svg";

import useApiClient from "../../api/client";
import config from "../../config/env";
import BatteryPill from "../../components/battery/battery";

function asImageUrl(url) {
    if (!url) return "";
    if (/^https?:\/\//i.test(url)) return url;
    const base = String(config?.apiImg || "").replace(/\/+$/, "");
    if (!base) return url;
    return url.startsWith("/") ? `${base}${url}` : `${base}/${url}`;
}

function formatThaiBuddhistDate(value) {
    if (!value) return "";
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return String(value);
    const dd = String(d.getDate()).padStart(2, "0");
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const yyyy = String(d.getFullYear() + 543);
    return `${dd}-${mm}-${yyyy}`;
}

function joinImgUrl(base, path) {
    if (!path) return "";
    if (/^https?:\/\//i.test(path)) return path;
    const b = String(base || "").replace(/\/+$/, "");
    const p = String(path || "").replace(/^\/+/, "");
    return b && p ? `${b}/${p}` : "";
}

function resolveImageUrl(image) {
    if (!image) return "";
    const path =
        image?.formats?.large?.url ||
        image?.formats?.medium?.url ||
        image?.formats?.small?.url ||
        image?.formats?.thumbnail?.url ||
        image?.url ||
        image?.attributes?.formats?.large?.url ||
        image?.attributes?.formats?.medium?.url ||
        image?.attributes?.formats?.small?.url ||
        image?.attributes?.formats?.thumbnail?.url ||
        image?.attributes?.url ||
        image?.data?.attributes?.formats?.large?.url ||
        image?.data?.attributes?.formats?.medium?.url ||
        image?.data?.attributes?.formats?.small?.url ||
        image?.data?.attributes?.formats?.thumbnail?.url ||
        image?.data?.attributes?.url ||
        "";
    return joinImgUrl(config.apiImg, path);
}

function normalizeExternalUrl(url) {
    const value = String(url || "").trim();
    if (!value) return "";
    if (/^https?:\/\//i.test(value)) return value;
    return `https://${value}`;
}

function pickImageUrls(product) {
    const imgs = product?.images;
    if (!Array.isArray(imgs) || imgs.length === 0) return [];
    return imgs
        .map((im) => resolveImageUrl(im))
        .filter(Boolean);
}

function cx(...xs) {
    return xs.filter(Boolean).join(" ");
}

function isPublicAvailableStatus(value) {
    const status = String(value || "").trim().toLowerCase();
    return ["available", "ready", "in_stock"].includes(status);
}

/** ---------- Horizontal Thumbnail Gallery ---------- */
function ThumbGallery({
    images = [],
    activeIndex,
    onToggle,
    className = "",
}) {
    const scrollerRef = useRef(null);

    // ---- Drag to scroll (desktop) ----
    const drag = useRef({
        isDown: false,
        startX: 0,
        scrollLeft: 0,
        moved: false,
    });

    const onMouseDown = (e) => {
        const el = scrollerRef.current;
        if (!el) return;
        drag.current.isDown = true;
        drag.current.moved = false;
        drag.current.startX = e.pageX - el.offsetLeft;
        drag.current.scrollLeft = el.scrollLeft;
    };

    const onMouseLeave = () => {
        drag.current.isDown = false;
    };

    const onMouseUp = () => {
        drag.current.isDown = false;
    };

    const onMouseMove = (e) => {
        const el = scrollerRef.current;
        if (!el || !drag.current.isDown) return;
        e.preventDefault();
        const x = e.pageX - el.offsetLeft;
        const walk = (x - drag.current.startX) * 1.2; // speed
        if (Math.abs(walk) > 3) drag.current.moved = true;
        el.scrollLeft = drag.current.scrollLeft - walk;
    };

    // กันคลิกตอนลาก (ไม่ให้ click toggle)
    const safeClick = (idx) => {
        if (drag.current.moved) return;
        onToggle?.(idx);
    };

    const scrollByPx = (px) => {
        scrollerRef.current?.scrollBy({ left: px, behavior: "smooth" });
    };

    // ---- คุมให้เห็น 3 รูปครึ่ง ----
    // w-[96px] ต่อรูป + gap 12px => 3.5 รูปกำลังพอดีใน container max-w ~ 380-420
    // (ถ้าต้องการใหญ่/เล็กกว่านี้ บอกได้)
    const ITEM_W = 96;
    const GAP = 12;
    const STEP = (ITEM_W + GAP) * 3; // กดลูกศรเลื่อนทีละ ~3 รูป

    return (
        <div className={cx("relative", className)}>
            {/* ✅ PC arrows */}
            <button
                type="button"
                onClick={() => scrollByPx(-STEP)}
                className="hidden md:inline-flex absolute -left-3 top-1/2 -translate-y-1/2 z-10 h-10 w-10 items-center justify-center rounded-full border border-slate-200 bg-white shadow-sm hover:bg-slate-50 active:scale-[0.98]"
                aria-label="เลื่อนซ้าย"
            >
                <ChevronLeft />
            </button>

            <button
                type="button"
                onClick={() => scrollByPx(STEP)}
                className="hidden md:inline-flex absolute -right-3 top-1/2 -translate-y-1/2 z-10 h-10 w-10 items-center justify-center rounded-full border border-slate-200 bg-white shadow-sm hover:bg-slate-50 active:scale-[0.98]"
                aria-label="เลื่อนขวา"
            >
                <ChevronRight />
            </button>

            {/* ✅ Scroll container */}
            <div
                ref={scrollerRef}
                className={cx(
                    "flex gap-3 overflow-x-auto no-scrollbar",
                    "scroll-smooth",
                    "cursor-grab active:cursor-grabbing",
                    "select-none"
                )}
                onMouseDown={onMouseDown}
                onMouseLeave={onMouseLeave}
                onMouseUp={onMouseUp}
                onMouseMove={onMouseMove}
            >
                {images.map((src, idx) => {
                    const active = activeIndex === idx;

                    return (
                        <button
                            key={idx}
                            type="button"
                            onClick={() => safeClick(idx)}
                            className={cx(
                                "flex-shrink-0 rounded-xl overflow-hidden border-2 bg-slate-100",
                                active ? "border-[#047DED] border-[4px]" : "border-transparent hover:border-[#047DED4D]"
                            )}
                            style={{ width: ITEM_W }}
                            aria-pressed={active}
                            title={active ? "กดอีกครั้งเพื่อยกเลิก" : "เลือกภาพนี้"}
                        >
                            <img
                                src={src}
                                alt={`thumb-${idx + 1}`}
                                className="h-[84px] w-full object-cover"
                                draggable={false}
                            />
                        </button>
                    );
                })}

                {/* ✅ เผื่อท้ายเล็กน้อยให้เห็น “ครึ่งรูป” ได้สวย */}
                <div className="flex-shrink-0 w-3" />
            </div>
        </div>
    );
}

export default function ProductDetailPage() {
    const nav = useNavigate();
    const { id } = useParams();

    const api = useApiClient();

    const [entity, setEntity] = useState(null);
    const [loading, setLoading] = useState(false);
    const [loadError, setLoadError] = useState("");

    useEffect(() => {
        let ignore = false;

        (async () => {
            try {
                setLoading(true);
                setLoadError("");

                const productId = decodeURIComponent(String(id || "").trim());
                if (!productId) {
                    setEntity(null);
                    setLoadError("ไม่พบสินค้า");
                    return;
                }

                let p = null;

                try {
                    const res = await api.getById("products", productId, {
                        params: { populate: "*" },
                    });
                    p = res?.data?.data || null;
                } catch {
                    p = null;
                }

                if (!p) {
                    const fallbackRes = await api.get("products", {
                        "filters[$or][0][id][$eq]": productId,
                        "filters[$or][1][documentId][$eq]": productId,
                        "pagination[page]": 1,
                        "pagination[pageSize]": 1,
                        populate: "*",
                    });
                    p = fallbackRes?.data?.data?.[0] || null;
                }

                if (ignore) return;

                if (!p) {
                    setEntity(null);
                    setLoadError("ไม่พบสินค้า");
                    return;
                }

                if (!isPublicAvailableStatus(p?.product_status)) {
                    setEntity(null);
                    setLoadError("ไม่พบสินค้า");
                    return;
                }

                setEntity(p);
            } catch (e) {
                console.error(e);
                if (!ignore) {
                    setEntity(null);
                    setLoadError("โหลดข้อมูลไม่สำเร็จ");
                }
            } finally {
                if (!ignore) setLoading(false);
            }
        })();

        return () => {
            ignore = true;
        };
    }, [id]);

    const item = useMemo(() => {
        const p = entity;
        const productName = String(p?.product_name || p?.model?.category?.name || "").trim();
        const modelName = String(p?.model?.name || "").trim();
        const title = productName || "-";
        const colorName = p?.color?.name || "-";
        const capText = p?.capacity?.size || p?.capacity?.name || "-";
        const price = Number(p?.sell_price ?? 0) || Number(p?.buy_price ?? 0) || 0;
        const isOnsale = Boolean(p?.is_onsale);
        const onsaleDiscount = Number(p?.onsale_discount ?? 0) || 0;
        const warrantyInfo = String(p?.warranty_info || "").trim();
        const warrantyEndDate = formatThaiBuddhistDate(p?.warranty_end_date || p?.warranty_date || "");
        const warranty =
            warrantyInfo === "ประกันศูนย์" && warrantyEndDate
                ? `ประกันศูนย์ถึง ${warrantyEndDate}`
                : warrantyInfo || warrantyEndDate || "-";
        const installmentRate = String(p?.installment_plan || "").trim();
        const desc = String(p?.website_description || p?.description || "").trim();
        const lines = desc ? desc.split("\n").map((s) => s.trim()).filter(Boolean) : [];

        const images = pickImageUrls(p);

        return {
            id,
            title,
            price,
            isOnsale,
            onsaleDiscount,
            batteryHealth: p?.battery_health,
            usage: String(p?.usage || ""),
            color: colorName,
            capacity: capText,
            warranty: String(warranty || "-"),
            installmentRate,
            images,
            image: images?.[0] || "",
            detail: lines.length ? lines : ["-"],
            facebookRef: normalizeExternalUrl(p?.facebook_ref),
        };
    }, [entity, id]);

    // ✅ active thumbnail: null = โหมดเดิม (ใช้รูปหลัก)
    const [activeIndex, setActiveIndex] = useState(null);

    // ✅ เปลี่ยนสินค้า (id) ให้ reset
    useEffect(() => setActiveIndex(null), [id]);

    const mainImage = activeIndex == null ? item.image : item.images?.[activeIndex] || item.image;
    const showOnsalePrice =
        Boolean(item?.isOnsale) &&
        Number(item?.onsaleDiscount ?? 0) > 0 &&
        Number(item?.onsaleDiscount ?? 0) !== Number(item?.price ?? 0);

    const toggleThumb = (idx) => {
        setActiveIndex((prev) => (prev === idx ? null : idx));
    };

    const openFacebookRef = () => {
        if (!item.facebookRef) return;
        window.open(item.facebookRef, "_blank", "noopener,noreferrer");
    };

    if (loading) {
        return (
            <div className="w-full bg-[#1B3147B2] backdrop-blur-xl h-[55px]">
                <div className="h-[50px]" />
                <div className="mx-auto max-w-6xl px-6 py-10 text-sm font-semibold text-white">
                    กำลังโหลด...
                </div>
            </div>
        );
    }

    if (loadError) {
        return (
            <div className="w-full bg-[#1B3147B2] backdrop-blur-xl h-[55px]">
                <div className="h-[50px]" />
                <div className="mx-auto max-w-6xl px-6 py-10">
                    <div className="text-sm font-semibold text-white">{loadError}</div>
                    <button
                        type="button"
                        onClick={() => nav(-1)}
                        className="mt-4 inline-flex h-10 items-center justify-center rounded-xl bg-white px-5 text-sm font-bold text-slate-900"
                    >
                        กลับ
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="w-full bg-[#1B3147B2] backdrop-blur-xl h-[55px]">
            {/* ถ้ามี header ของ public ใส่ได้ */}
            {/* <PublicHeader /> */}

            <div className="h-[50px]" />
            <div className="mx-auto max-w-6xl px-6 py-10">
                <div className="text-sm text-slate-500 mb-4">
                    <span
                        className="cursor-pointer text-slate-600 hover:text-[#047DED] hover:underline"
                        onClick={() => nav(-1)}
                    >
                        สินค้า
                    </span>
                    <span className="mx-2">/</span>
                    <span className="text-[#047DED] font-semibold">รายละเอียด</span>
                </div>

                <div className="text-3xl font-medium text-slate-900 mb-6">รายละเอียดสินค้า</div>

                <div className="grid grid-cols-12 sm:gap-2 md:gap-6 items-stretch">
                    {/* Image */}
                    <div className="col-span-12 md:col-span-6 pb-10 md:pb-0 h-full">
                        <div className="rounded-2xl overflow-hidden bg-slate-100">
                            {mainImage ? (
                                <img src={mainImage} alt={item.title} className="w-full h-[420px] md:h-[520px] object-cover" />
                            ) : (
                                <div className="w-full h-[420px] md:h-[520px] flex items-center justify-center text-slate-400">
                                    ไม่มีรูปภาพ
                                </div>
                            )}
                        </div>

                        {/* ✅ thumbnails */}
                        <div className="mt-4">
                            <ThumbGallery images={item.images || []} activeIndex={activeIndex} onToggle={toggleThumb} />
                        </div>
                    </div>

                    {/* Info */}
                    <div className="col-span-12 md:col-span-6 h-full">
                        <div className="h-full flex flex-col rounded-2xl bg-white sm:px-0 justify-start">
                            <div className="text-3xl font-extrabold text-slate-900">{item.title}</div>
                            <div className="mt-2 flex items-center gap-3">
                                {showOnsalePrice ? (
                                    <>
                                        <div className="text-2xl font-extrabold text-[#A92B2B] line-through">
                                            ฿{Number(item.price || 0).toLocaleString()}
                                        </div>
                                        <div className="text-2xl font-extrabold text-[#047DED]">
                                            ฿{Number(item.onsaleDiscount || 0).toLocaleString()}
                                        </div>
                                    </>
                                ) : (
                                    <div className="text-2xl font-extrabold text-[#047DED]">
                                        ฿{Number(item.price || 0).toLocaleString()}
                                    </div>
                                )}
                            </div>

                            {/* {item.batteryHealth != null && item.batteryHealth !== "" && (
                                <div className="mt-3 flex items-center gap-2 text-slate-600">
                                    <BatteryPill value={item.batteryHealth} width={49} />
                                    <span>{item.usage || "-"}</span>
                                </div>
                            )} */}
                            <div className="text-slate-600 mt-2">
                                {item.color}
                                {item.installmentRate ? <span className="ml-3">{item.installmentRate}</span> : null}
                            </div>
                            <div className="text-slate-600 mt-1">{item.capacity}</div>
                            <div className="text-slate-600 mt-1">{item.warranty}</div>

                            {/* Detail (scroll ถ้าเกิน) */}
                            <div className="mt-4 rounded-xl bg-[#F5F5F5] p-4 overflow-auto max-h-[280px]">
                                <div className="font-semibold text-slate-800 mb-2">รายละเอียดสินค้า</div>
                                <ul className="space-y-1 text-sm text-slate-700 list-disc pl-5">
                                    {item.detail.map((t, i) => (
                                        <li key={i}>{t}</li>
                                    ))}
                                </ul>
                            </div>

                            {/* Buttons ติดล่างสุด */}
                            <div className="pt-6 flex gap-3 sm:flex-col md:flex-row ">
                                <button onClick={() => window.open('https://lin.ee/VUW5WFX', '_blank')} className="flex-1 sm:py-3 md:py-0 inline-flex items-center justify-center gap-2 h-11 rounded-xl bg-white text-[#30CA17] border-[2px] border-[#30CA1733] font-medium hover:shadow-[#30CA1733] hover:bg-[#30CA171A] transition">
                                    <Line className="w-5 h-5 text-[#30CA17]" />
                                    ติดต่อ Line OA
                                </button>

                                <button
                                    type="button"
                                    onClick={openFacebookRef}
                                    disabled={!item.facebookRef}
                                    className={cx(
                                        "flex-1 sm:py-3 md:py-0 inline-flex items-center justify-center gap-2 h-11 rounded-xl text-white font-medium transition",
                                        item.facebookRef
                                            ? "bg-[#047DED] hover:shadow-[#047DED66] hover:bg-[#3EA2FE]"
                                            : "bg-slate-300 cursor-not-allowed"
                                    )}
                                >
                                    รายละเอียดเพิ่มเติม
                                    <Share2 className="w-5 h-5" />
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
